import { Flex } from 'antd';
import ErrorMessage from '../error-message/ErrorMessage';
import styles from './FloatingSelect.module.scss';
import { useEffect, useRef, useState } from 'react';
import { SelectArrowIcon } from '../all-icons/custom-icons';
import SelectedOptions from './SelectedOptions';
import OptionsDropdown from './OptionsDropdown';
import { IF } from 'common';
import Label from 'common/label/Label';

const FloatingSelect = ({
  handleChange,
  value = [],
  options,
  label,
  error,
  name,
  type,
  showSlug = false,
  onBlur = () => {},
  onFocus = () => {},
  extraOption = {},
  placeholder,
  customPlaceholder = null,
  singleSelect = false,
  searchType = '',
  small_height = false,
  disabled = false,
  containerStyle,
  customOptionRender = null,
  customSelectedOptionsRender,
  isElastic = false,
  autoComplete = 'off',
  useFloatingLabel = true,
  labelClassName = '',
  className,
}) => {
  const inputRef = useRef(null);
  const inputWrapRef = useRef(null);
  const optionRef = useRef(null);
  const optionRefs = useRef([]);

  const [isOpen, setIsOpen] = useState(false);
  const [placehold, setPlacehold] = useState(placeholder);
  const [inputWidth, setInputWidth] = useState(0);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  useEffect(() => {
    if (singleSelect) {
      setPlacehold(value?.[0]?.label || placeholder || '');
      return;
    } else {
      setPlacehold('');
    }
    if (inputWrapRef.current) {
      setInputWidth(inputWrapRef.current?.offsetWidth);
    }
  }, [singleSelect ? value?.[0]?.id : value?.length]);

  const handleBlur = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputWrapRef.current &&
        !inputWrapRef.current.contains(event.target) &&
        isOpen &&
        optionRef.current &&
        !optionRef.current.contains(event.target)
      ) {
        handleBlur();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const arrowStyles = {
    position: 'absolute',
    right: '10px',
    top: small_height ? '11px' : useFloatingLabel ? '15px' : '36px',
    transform: isOpen ? 'rotate(0deg)' : 'rotate(-180deg)',
    transition: 'transform 0.2s ease-in-out',
    color: 'var(--user-main-text-2)',
  };

  useEffect(() => {
    if (highlightedIndex !== -1 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex]);

  return (
    <Flex ref={inputWrapRef} vertical gap={3} className="w-full">
      <div
        className={`${styles['float-input']} ${placeholder !== placehold && styles.placeholder} ${value?.length > 0 && styles.hasSelections} ${!!error && styles.error} ${small_height && styles.small_height} `}
      >
        <IF condition={!useFloatingLabel}>
          <Label className={labelClassName}>{label}</Label>
        </IF>
        <input
          ref={inputRef}
          onChange={() => {
            setIsOpen(true);
          }}
          value={placehold}
          placeholder={customPlaceholder ?? placehold}
          name={name}
          className={className}
          autoComplete={autoComplete}
          onBlur={onBlur}
          onFocus={() => {
            !isOpen && onFocus('');
            setIsOpen(true);
          }}
          type={type || 'text'}
          id={name}
          disabled={disabled}
        />
        {!disabled && (
          <SelectArrowIcon
            style={arrowStyles}
            onClick={() => {
              onFocus();
              setIsOpen(!isOpen);
            }}
          />
        )}
        <IF condition={!singleSelect}>
          <IF
            condition={!customSelectedOptionsRender}
            ELSE={customSelectedOptionsRender ? customSelectedOptionsRender({ handleChange, value, disabled }) : null}
          >
            <SelectedOptions
              small_height={small_height}
              handleChange={handleChange}
              value={value}
              inputWidth={inputWidth}
              disabled={disabled}
            />
          </IF>
        </IF>

        <IF condition={useFloatingLabel}>
          <label className={styles['float-label']} htmlFor={name}>
            {label}
          </label>
        </IF>
      </div>
      <OptionsDropdown
        isOpen={isOpen}
        inputRef={inputRef}
        optionRef={optionRef}
        optionRefs={optionRefs}
        singleSelect={singleSelect}
        value={value}
        setIsOpen={setIsOpen}
        handleChange={handleChange}
        extraOption={extraOption}
        searchType={searchType}
        showSlug={showSlug}
        highlightedIndex={highlightedIndex}
        setHighlightedIndex={setHighlightedIndex}
        options={options}
        containerStyle={containerStyle}
        customRender={customOptionRender}
        isElastic={isElastic}
        onFocus={onFocus}
      />
      <IF condition={error}>
        <ErrorMessage errorMessage={error} />
      </IF>
    </Flex>
  );
};

export default FloatingSelect;
